import { showConfirmModal, ConfirmModalStyle } from "./confirm-modal";
import { getLocalizedString } from "@glide/localization";
import { CancelledError, NetworkOfflineError, FileReadError, FileSizeLimitExceededError } from "./upload-handlers";
import { type UploadFileResponse, isUploadFileResponseError } from "./types";

export async function showCancelUploadModal(): Promise<boolean> {
    return (
        (await showConfirmModal({
            title: getLocalizedString("cancelUpload"),
            accept: getLocalizedString("cancel"),
            cancel: getLocalizedString("continue"),
            description: getLocalizedString("uploadProgressWillBeLost"),
            style: ConfirmModalStyle.Delete,
        })) ?? false
    );
}

// Returns "true" if you should retry the operation.
// "Cancel" is considered "accept" here because the proper Cancel
// modal uses "Continue" as "cancel" and "Cancel" as "accept".
// If these two modals start conflicting over each other,
// you end up with a user trying to continue a near-cancelled
// upload just in time to cancel a failed upload.
export async function showUploadFailedRetryModal(resp: UploadFileResponse): Promise<boolean> {
    if (
        !isUploadFileResponseError(resp) ||
        resp.error instanceof CancelledError ||
        resp.error instanceof FileSizeLimitExceededError
    ) {
        return false;
    }

    let title = getLocalizedString("uploadFailedTitle");
    let description: string;
    if (resp.quotaExceeded) {
        description = getLocalizedString("uploadQuotaReached");
    } else {
        description = getLocalizedString("uploadFailedDetails");
    }
    const { error } = resp;
    if (error instanceof NetworkOfflineError) {
        title = getLocalizedString("networkDownTitle");
        description = getLocalizedString("uploadNetworkDownDetails");
    } else if (error instanceof FileReadError) {
        title = getLocalizedString("fileReadErrorTitle");
        description = getLocalizedString("fileReadErrorDetails");
    }

    return !(
        (await showConfirmModal({
            title,
            description,
            cancel: resp.quotaExceeded ? undefined : getLocalizedString("retry"),
            accept: getLocalizedString("cancel"),
            style: ConfirmModalStyle.Delete,
        })) ?? false
    );
}
